import { componentFactoryOf } from 'vue-tsx-support';

import { JtnUiTypography, JtnUiBtnIcon, JtnUiPrimaryLoader } from '@jtnews/jtn-ui';
import { injectStylesMixin, tsStoreMixin } from '@jtnews/shared';

import { Slider } from '../../components/slider';

import styles from './widget-gallery-slider.styles.scss?module';

interface Events {
  onTitleClicked: void;
  onSlideChanged: {
    prev: number;
    current: number;
  };
  onNavBtnClicked: 'prev' | 'next';
  onSwiped: 'prev' | 'next';
}

type ButtonsPosition = {
  top?: string;
  bottom?: string;
};

type ComponentData = {
  sliderRendered: boolean;
  mediaHeight: number;
  mediaMaxHeight: number;
  isBeginning: boolean;
  isEnd: boolean;
  toolbarOffsetTop: number;
  activeIndex: number;
  sliderContainerHeight: number;
  sliderHeight: number;
  mediasArray: Element[];
  showThumbsLeftOverlay: boolean;
  showThumbsRightOverlay: boolean;
  previousSlide: number;
  isSwipe: boolean;
};

const THUMB_ITEM_HEIGHT = 116;

export default componentFactoryOf<Events>()
  .mixin(tsStoreMixin)
  .mixin(injectStylesMixin(styles))
  .create({
    name: 'WidgetGallerySlider',
    props: {
      showLoader: {
        type: Boolean,
        default: false
      },
      title: {
        type: String,
        default: ''
      },
      titleLink: {
        type: String,
        default: ''
      },
      theme: {
        type: String,
        default: ''
      },
      navButtonsType: {
        type: String as () => 'over_photo' | 'above_photo',
        default: 'over_photo'
      },
      blockPosition: {
        type: String,
        default: ''
      },
      hasThumbs: {
        type: Boolean,
        default: false
      },
      groupSize: {
        type: Number,
        default: 1
      },
      sliderContentClassName: {
        type: String,
        default: ''
      },
      mediaContentClassName: {
        type: String,
        default: ''
      },
      setVideoHeigth: {
        type: Boolean,
        default: false
      },
      sliderType: {
        type: String,
        default: ''
      },
      imageRatio: {
        type: Number,
        default: 16 / 9
      },
      hasPushFromLastSlide: {
        type: Boolean,
        default: false
      },
      hasBlackTheme: {
        type: Boolean,
        default: false
      },
      pageDots: {
        type: Boolean,
        default: false
      },
      initialSlideIndex: {
        type: Number,
        default: 0
      }
    },
    data(): ComponentData {
      return {
        sliderRendered: false,
        mediaHeight: 300,
        mediaMaxHeight: 300,
        isBeginning: true,
        isEnd: false,
        toolbarOffsetTop: 0,
        activeIndex: 0,
        sliderContainerHeight: 0,
        sliderHeight: 0,
        mediasArray: [],
        showThumbsLeftOverlay: false,
        showThumbsRightOverlay: true,
        previousSlide: 0,
        isSwipe: true
      };
    },
    computed: {
      isMobile(): boolean {
        return this.store.deviceInfo.isMobile;
      },
      isTablet(): boolean {
        return this.store.deviceInfo.isTablet;
      },
      loaderVisible(): boolean {
        return this.showLoader === true || this.sliderRendered == false;
      },
      arrowIconStroke(): string {
        return this.theme === 'dark' ? 'rgba(255, 255, 255, 0.6)' : '#8D96A1';
      },
      isRightColumnBlock(): boolean {
        return this.blockPosition === 'right-column';
      },
      toolbarButtonSize() {
        if (this.isRightColumnBlock) {
          return {
            size: 18,
            iconW: 18,
            iconH: 12
          };
        }

        if (this.isMobile && !this.isTablet) {
          return {
            size: 32,
            iconW: 17,
            iconH: 12
          };
        }

        return {
          size: 48,
          iconW: 20,
          iconH: 20
        };
      },
      buttonsPosition(): ButtonsPosition {
        if (this.sliderType == 'record-gallery' && this.isMobile) {
          return {
            bottom: '0',
            top: 'auto'
          };
        }

        if (this.isMobile && !this.isTablet) {
          return {
            top: '0'
          };
        }

        return {
          bottom: '0'
        };
      },
      lastSlideIndex(): number {
        if (this.mediasArray !== null) {
          return this.groupSize === 0
            ? this.mediasArray.length - 1
            : Math.floor(this.mediasArray.length / this.groupSize) - 1;
        }
        return 0;
      }
    },
    watch: {
      activeIndex() {
        if (this.hasThumbs) {
          this.checkThumbsOverlay();
        }
      }
    },
    updated() {
      if (
        this.$refs.sliderContainer &&
        this.$refs.toolbar &&
        this.sliderType == 'record-gallery'
      ) {
        this.toolbarOffsetTop =
          (this.$refs.sliderContainer as HTMLElement).offsetHeight -
          this.mediaMaxHeight -
          54;
      }
    },
    mounted() {
      this.$nextTick(() => {
        window.addEventListener('resize', () => this.calcComponentData());
        if (this.initialSlideIndex !== 0) {
          this.activeIndex = this.initialSlideIndex;
          this.updateSliderButtons(this.activeIndex);
        }
      });
    },
    beforeDestroy() {
      window.removeEventListener('resize', () => this.calcComponentData());
    },
    methods: {
      sliderReady() {
        this.sliderRendered = true;
        this.calcComponentData();
      },
      calcComponentData() {
        this.setMediaData();
        this.setSliderHeights();
      },
      setSliderHeights() {
        const contentArray = [
          ...(this.$refs.sliderContainer as HTMLElement).querySelectorAll(
            `.${this.sliderContentClassName}`
          )
        ];

        const mappedArray = this.isRightColumnBlock ? this.mediasArray : contentArray;

        this.sliderHeight = Math.max(
          ...mappedArray.map((value: Element) => value.clientHeight)
        );

        this.sliderContainerHeight =
          this.sliderHeight + (this.hasThumbs ? THUMB_ITEM_HEIGHT : 0);
      },
      setMediaData() {
        this.mediaMaxHeight = Math.floor(
          (this.$refs.widgetSliderWrapper as HTMLElement).clientWidth / this.imageRatio
        );

        this.mediasArray = [
          ...(this.$refs.sliderContainer as HTMLElement).querySelectorAll(
            `.${this.mediaContentClassName}`
          )
        ];

        // нужно для вычисления координаты расположения кнопок для переключения слайдов
        this.mediaHeight = this.sliderContainerHeight;

        if (this.isRightColumnBlock === false) {
          // forEach для NodeList нет в IE11,
          // поэтому обычным for'ом перебираем
          const styleName = this.setVideoHeigth ? 'height' : 'max-height';

          for (let i = 0; i < this.mediasArray.length - 1; i++) {
            (this.mediasArray[i] as HTMLElement).style[
              styleName
            ] = `${this.mediaMaxHeight}px`;
          }
        }
      },
      slideChanged(prev: number, current: number) {
        const slideChangeInfo = {
          prev,
          current
        };

        if (this.activeIndex !== current) {
          this.$emit('slideChanged', slideChangeInfo);
        }
      },
      setGroup(groupIndex: number) {
        this.slideChanged(this.activeIndex, groupIndex);

        this.activeIndex = groupIndex;
        this.updateSliderButtons(groupIndex);
        this.$emit('swipe');
      },
      updateSliderButtons(groupIndex = 0) {
        this.isBeginning = groupIndex === 0;
        this.isEnd = groupIndex === this.lastSlideIndex && !this.hasPushFromLastSlide;
      },
      prevSlide() {
        if (this.activeIndex !== 0) {
          const prevIndex = this.activeIndex - 1;
          this.slideChanged(this.activeIndex, prevIndex);

          this.activeIndex = prevIndex;
          this.isSwipe = false;
          this.$emit('navBtnClicked', 'prev');
        }
      },
      nextSlide() {
        if (this.activeIndex !== this.lastSlideIndex || this.hasPushFromLastSlide) {
          const nextIndex = this.activeIndex + 1;
          this.slideChanged(this.activeIndex, nextIndex);

          this.activeIndex = nextIndex;
          this.isSwipe = false;
          this.$emit('navBtnClicked', 'next');
        }
      },
      checkThumbsOverlay() {
        // нужно проверить, показывать ли оверлей слева
        if (this.activeIndex >= 2) {
          this.showThumbsLeftOverlay = true;
        } else {
          this.showThumbsLeftOverlay = false;
        }

        const slidesCount = this.mediasArray?.length || 0;
        // нужно проверить, показывать ли оверлей справа
        if (this.activeIndex <= slidesCount - 3) {
          this.showThumbsRightOverlay = true;
        } else {
          this.showThumbsRightOverlay = false;
        }
      },
      sendSwipedEvent(index: number) {
        if (index > this.previousSlide && this.isSwipe) {
          this.$emit('swiped', 'next');
        }
        if (index < this.previousSlide && this.isSwipe) {
          this.$emit('swiped', 'prev');
        }
        this.previousSlide = index;
        this.isSwipe = true;
      }
    },
    render() {
      return (
        <div
          ref="widgetSliderWrapper"
          class={[
            styles.widgetSliderWrapper,
            this.isRightColumnBlock ? styles.rightColumnWrapper : ''
          ]}
        >
          {this.loaderVisible ? <JtnUiPrimaryLoader /> : null}

          <div
            class={[
              styles.titleWrapper,
              this.isRightColumnBlock ? styles.rightColumnTitle : ''
            ]}
          >
            <JtnUiTypography
              class={styles.title}
              tag="h2"
              type="font-vijet-title"
              linkType="link-blue"
            >
              {this.titleLink.length > 0 ? (
                <a
                  class={styles.titleLink}
                  href={this.titleLink}
                  title={this.titleLink}
                  onClick={() => this.$emit('titleClicked')}
                  domPropsInnerHTML={this.title}
                />
              ) : (
                <span>{this.title}</span>
              )}
            </JtnUiTypography>

            {this.theme.length > 0 ? (
              <a
                class={styles.arrowIcon}
                href={this.titleLink}
                title={this.title}
                onClick={() => this.$emit('titleClicked')}
              >
                <svg
                  style={{ color: this.arrowIconStroke }}
                  strokeWidth={2}
                  height={18}
                  width={18}
                >
                  <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-arrow-right"></use>
                </svg>
              </a>
            ) : null}
          </div>

          <div
            class={[styles.sliderContainer, this.sliderRendered ? '' : styles.unvisible]}
            style={{ height: `${this.sliderContainerHeight}px` }}
            ref="sliderContainer"
          >
            <Slider
              style={{ height: `${this.sliderHeight}px` }}
              initialIndex={this.initialSlideIndex}
              currentGroup={this.activeIndex}
              groupSize={this.groupSize}
              hasThumbs={this.hasThumbs}
              pageDots={this.pageDots}
              isDraggable={this.isMobile}
              onSlideChanged={groupIndex => this.setGroup(groupIndex)}
              onSliderSettled={(index: number) => this.sendSwipedEvent(index)}
              onReady={() => this.sliderReady()}
            >
              <template slot="slides">{this.$slots.slides}</template>
              <template slot="thumbs">{this.$slots.thumbs}</template>
            </Slider>

            {this.showThumbsLeftOverlay && this.hasThumbs ? (
              <div
                class={[
                  styles.overlay,
                  styles.left,
                  this.hasBlackTheme ? styles.black : ''
                ]}
              />
            ) : null}

            {this.showThumbsRightOverlay && this.hasThumbs ? (
              <div
                class={[
                  styles.overlay,
                  styles.right,
                  this.hasBlackTheme ? styles.black : ''
                ]}
              />
            ) : null}

            {this.navButtonsType === 'over_photo' && this.sliderRendered ? (
              <div class={styles.toolbarOver} ref="toolbar" style={this.buttonsPosition}>
                <JtnUiBtnIcon
                  class={[styles.toolbarBtn, styles.left]}
                  btnType="button-other-type-4"
                  size={this.toolbarButtonSize.size}
                  disabled={this.isBeginning}
                  onClick={() => this.prevSlide()}
                >
                  <svg
                    class={styles.iconLeft}
                    width={this.toolbarButtonSize.iconW}
                    height={this.toolbarButtonSize.iconH}
                  >
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-direction"></use>
                  </svg>
                </JtnUiBtnIcon>

                <JtnUiBtnIcon
                  class={[styles.toolbarBtn, styles.right]}
                  btnType="button-other-type-4"
                  size={this.toolbarButtonSize.size}
                  disabled={this.isEnd}
                  onClick={() => this.nextSlide()}
                >
                  <svg
                    width={this.toolbarButtonSize.iconW}
                    height={this.toolbarButtonSize.iconH}
                  >
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-direction"></use>
                  </svg>
                </JtnUiBtnIcon>
              </div>
            ) : null}
          </div>

          {this.navButtonsType === 'above_photo' && this.sliderRendered ? (
            <div
              class={styles.toolbarAbove}
              ref="toolbar"
              style={`padding-top: ${this.toolbarOffsetTop}px`}
            >
              <div class={styles.toolbarButtons}>
                <JtnUiBtnIcon
                  class={[styles.toolbarBtn, styles.left]}
                  btnType="button-other-type-3"
                  size={this.toolbarButtonSize.size}
                  disabled={this.isBeginning}
                  onClick={() => this.prevSlide()}
                >
                  <svg
                    class={styles.iconLeft}
                    width={this.toolbarButtonSize.iconW}
                    height={this.toolbarButtonSize.iconH}
                  >
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-direction"></use>
                  </svg>
                </JtnUiBtnIcon>

                <JtnUiBtnIcon
                  class={[styles.toolbarBtn, styles.right]}
                  btnType="button-other-type-3"
                  size={this.toolbarButtonSize.size}
                  disabled={this.isEnd}
                  onClick={() => this.nextSlide()}
                >
                  <svg
                    width={this.toolbarButtonSize.iconW}
                    height={this.toolbarButtonSize.iconH}
                  >
                    <use xlinkHref="/dist/legacy/svg-sprites/jtn-critical.42b7545660e4f467e75d4b37a02533e6.svg#jtn-critical-direction"></use>
                  </svg>
                </JtnUiBtnIcon>
              </div>
            </div>
          ) : null}
        </div>
      );
    }
  });
